import type { InteractionContext, MessageChannel, MessageTemplate } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Drawer,
  DrawerContent,
  EmptyState,
  FormField,
  Input,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  SettingSwitch,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Textarea,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus, Settings2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import {
  createTemplate,
  crmKeys,
  crmSettingsQueryOptions,
  deleteTemplate,
  templatesQueryOptions,
  updateCrmSettings,
  updateTemplate,
} from '@/features/crm/queries';
import { errorMessage } from '@/lib/errors';

export const Route = createFileRoute('/_app/crm/settings')({ component: CrmSettingsPage });

const CHANNELS: MessageChannel[] = ['ANY', 'EMAIL', 'WHATSAPP', 'SMS'];
const CONTEXTS: InteractionContext[] = ['BIRTHDAY', 'INACTIVE', 'SEGMENT', 'MANUAL', 'SERVICE'];

/** Réglages CRM et modèles de message (ADR-025, D-H67, R-CRM-08). */
function CrmSettingsPage() {
  const { t } = useTranslation(['crm', 'common']);
  const canManage = useCan('crm.manage');
  const queryClient = useQueryClient();
  const settings = useQuery(crmSettingsQueryOptions(canManage));
  const templates = useQuery(templatesQueryOptions(canManage));
  const [inactive, setInactive] = useState('90');
  const [birthday, setBirthday] = useState('30');
  const [editing, setEditing] = useState<MessageTemplate | null | 'new'>(null);
  useEffect(() => {
    if (!settings.data) return;
    setInactive(String(settings.data.inactiveAfterDays));
    setBirthday(String(settings.data.birthdayWindowDays));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      updateCrmSettings({
        inactiveAfterDays: Number(inactive),
        birthdayWindowDays: Number(birthday),
        version: settings.data?.version ?? 0,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
      toast.success(t('crm:settings.saved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.templates });
      toast.success(t('crm:settings.deleted'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canManage) {
    return (
      <Container>
        <PageHeader title={t('crm:settings.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:settings.title')}
        subtitle={t('crm:settings.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:settings.title') },
        ]}
      />
      <div className="stack">
        <Card>
          <CardHeader
            title={t('crm:settings.title')}
            icon={<Settings2 className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
          />
          <CardBody>
            {settings.isPending ? (
              <Skeleton height={80} />
            ) : (
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  save.mutate();
                }}
              >
                <div className="grid grid-2">
                  <FormField label={t('crm:settings.inactiveAfterDays')} required>
                    {(field) => (
                      <Input
                        id={field.id}
                        inputMode="numeric"
                        value={inactive}
                        onChange={(e) => setInactive(e.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField label={t('crm:settings.birthdayWindowDays')} required>
                    {(field) => (
                      <Input
                        id={field.id}
                        inputMode="numeric"
                        value={birthday}
                        onChange={(e) => setBirthday(e.target.value)}
                      />
                    )}
                  </FormField>
                </div>
                <div>
                  <Button variant="primary" type="submit" disabled={save.isPending}>
                    {t('crm:settings.save')}
                  </Button>
                </div>
              </form>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('crm:settings.templates')}
            actions={
              <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
                <Plus className="ico ico-14" aria-hidden="true" />
                {t('crm:settings.newTemplate')}
              </Button>
            }
          />
          <CardBody density="flush">
            {templates.isPending ? (
              <div className="p-4">
                <Skeleton height={80} />
              </div>
            ) : (templates.data ?? []).length === 0 ? (
              <EmptyState title={t('crm:settings.templatesEmpty')} />
            ) : (
              <TableWrap>
                <Table>
                  <caption className="sr-only">{t('crm:settings.templates')}</caption>
                  <thead>
                    <tr>
                      <Th>{t('crm:settings.template.name')}</Th>
                      <Th>{t('crm:settings.template.context')}</Th>
                      <Th>{t('crm:settings.template.channel')}</Th>
                      <Th>{t('crm:settings.template.isActive')}</Th>
                      <Th>{t('common:table.actions')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(templates.data ?? []).map((template) => (
                      <Tr key={template.id}>
                        <Td>
                          <div className="strong">{template.name}</div>
                          <div className="muted t-12 mono">{template.code}</div>
                        </Td>
                        <Td>{t(`crm:interactions.contexts.${template.context}`)}</Td>
                        <Td>{t(`crm:settings.channels.${template.channel}`)}</Td>
                        <Td>
                          <Badge tone={template.isActive ? 'green' : 'neutral'}>
                            {template.isActive ? t('common:actions.yes') : t('common:actions.no')}
                          </Badge>
                        </Td>
                        <Td>
                          <div className="row" style={{ gap: 4 }}>
                            <Button variant="ghost" size="sm" onClick={() => setEditing(template)}>
                              {t('common:actions.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={remove.isPending}
                              onClick={() => remove.mutate(template.id)}
                            >
                              <Trash2 className="ico ico-14" aria-hidden="true" />
                              {t('crm:settings.deleteTemplate')}
                            </Button>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
        </Card>
      </div>
      {editing ? (
        <TemplateDrawer
          template={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      ) : null}
    </Container>
  );
}

function TemplateDrawer({
  template,
  open,
  onOpenChange,
}: {
  template: MessageTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['crm', 'common']);
  const queryClient = useQueryClient();
  const [code, setCode] = useState(template?.code ?? '');
  const [name, setName] = useState(template?.name ?? '');
  const [channel, setChannel] = useState<MessageChannel>(template?.channel ?? 'ANY');
  const [context, setContext] = useState<InteractionContext>(template?.context ?? 'MANUAL');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const mutation = useMutation({
    mutationFn: () =>
      template
        ? updateTemplate(template.id, {
            code: code.trim(),
            name: name.trim(),
            channel,
            context,
            subject: subject.trim() || null,
            body: body.trim(),
            isActive,
            version: template.version,
          })
        : createTemplate({
            code: code.trim(),
            name: name.trim(),
            channel,
            context,
            subject: subject.trim() || null,
            body: body.trim(),
            isActive,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.templates });
      toast.success(t('crm:settings.templateSaved'));
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const valid = code.trim().length >= 2 && name.trim().length > 0 && body.trim().length > 0;
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        title={template ? t('crm:settings.editTemplate') : t('crm:settings.newTemplate')}
        closeLabel={t('common:actions.close')}
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) mutation.mutate();
        }}
        footer={
          <>
            <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={!valid || mutation.isPending}>
              {t('common:actions.save')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="grid grid-2">
            <FormField label={t('crm:settings.template.code')} required>
              {(field) => (
                <Input
                  id={field.id}
                  value={code}
                  maxLength={40}
                  onChange={(e) => setCode(e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:settings.template.name')} required>
              {(field) => (
                <Input
                  id={field.id}
                  value={name}
                  maxLength={120}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:settings.template.channel')}>
              {(field) => (
                <NativeSelect
                  id={field.id}
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as MessageChannel)}
                >
                  {CHANNELS.map((value) => (
                    <option key={value} value={value}>
                      {t(`crm:settings.channels.${value}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>
            <FormField label={t('crm:settings.template.context')}>
              {(field) => (
                <NativeSelect
                  id={field.id}
                  value={context}
                  onChange={(e) => setContext(e.target.value as InteractionContext)}
                >
                  {CONTEXTS.map((value) => (
                    <option key={value} value={value}>
                      {t(`crm:interactions.contexts.${value}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>
          </div>
          <FormField label={t('crm:settings.template.subject')}>
            {(field) => (
              <Input
                id={field.id}
                value={subject}
                maxLength={200}
                onChange={(e) => setSubject(e.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('crm:settings.template.body')} required hint={t('crm:messages.hint')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={6}
                value={body}
                maxLength={4000}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </FormField>
          <SettingSwitch
            label={t('crm:settings.template.isActive')}
            checked={isActive}
            onCheckedChange={(v) => setIsActive(Boolean(v))}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
